import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Image, Gavel, Users, DollarSign, Mail, Plus, Trash2, Edit, 
  LayoutDashboard, MailOpen, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Artwork form
  const [artworkModal, setArtworkModal] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [artworkForm, setArtworkForm] = useState({
    title: '',
    description: '',
    image_url: '',
    year: '',
    medium: '',
    dimensions: ''
  });

  // Auction form
  const [auctionModal, setAuctionModal] = useState(false);
  const [auctionForm, setAuctionForm] = useState({
    artwork_id: '',
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    starting_bid: ''
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, artworksRes, auctionsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/api/artworks`),
        axios.get(`${API}/api/auctions`),
        axios.get(`${API}/api/contact/messages`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setArtworks(artworksRes.data);
      setAuctions(auctionsRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleArtworkSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingArtwork) {
        await axios.put(`${API}/api/artworks/${editingArtwork.id}`, artworkForm, { withCredentials: true });
        toast.success('Artwork updated');
      } else {
        await axios.post(`${API}/api/artworks`, artworkForm, { withCredentials: true });
        toast.success('Artwork created');
      }
      setArtworkModal(false);
      setEditingArtwork(null);
      setArtworkForm({ title: '', description: '', image_url: '', year: '', medium: '', dimensions: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save artwork');
    }
  };

  const handleDeleteArtwork = async (id) => {
    if (!window.confirm('Delete this artwork?')) return;
    try {
      await axios.delete(`${API}/api/artworks/${id}`, { withCredentials: true });
      toast.success('Artwork deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete artwork');
    }
  };

  const handleAuctionSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/auctions`, {
        ...auctionForm,
        start_date: new Date(auctionForm.start_date).toISOString(),
        end_date: new Date(auctionForm.end_date).toISOString(),
        starting_bid: parseFloat(auctionForm.starting_bid)
      }, { withCredentials: true });
      toast.success('Auction created');
      setAuctionModal(false);
      setAuctionForm({ artwork_id: '', title: '', description: '', start_date: '', end_date: '', starting_bid: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create auction');
    }
  };

  const handleDeleteAuction = async (id) => {
    if (!window.confirm('Delete this auction?')) return;
    try {
      await axios.delete(`${API}/api/auctions/${id}`, { withCredentials: true });
      toast.success('Auction deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete auction');
    }
  };

  const openEditArtwork = (artwork) => {
    setEditingArtwork(artwork);
    setArtworkForm({
      title: artwork.title || '',
      description: artwork.description || '',
      image_url: artwork.image_url || '',
      year: artwork.year || '',
      medium: artwork.medium || '',
      dimensions: artwork.dimensions || ''
    });
    setArtworkModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-[#C5A059] font-serif text-2xl">Loading dashboard...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'artworks', label: 'Artworks', icon: Image },
    { id: 'auctions', label: 'Auctions', icon: Gavel },
    { id: 'messages', label: 'Messages', icon: Mail }
  ];

  return (
    <div className="min-h-screen pt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Header */}
        <div className="mb-12">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#525252] mb-4">
            Admin
          </p>
          <h1 className="font-serif text-4xl text-[#F5F5F0] tracking-tight">
            Dashboard
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#C5A059] text-[#0A0A0A]'
                  : 'text-[#A3A3A3] hover:text-[#F5F5F0] border border-white/10'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} />
              {tab.label}
              {tab.id === 'messages' && stats?.unread_messages > 0 && (
                <span className="ml-2 w-5 h-5 bg-[#D84B79] text-white text-[10px] rounded-full flex items-center justify-center">
                  {stats.unread_messages}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="admin-card">
              <Image size={24} className="text-[#C5A059] mb-4" />
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-2">
                Total Artworks
              </p>
              <p className="font-serif text-4xl text-[#F5F5F0]">{stats.total_artworks}</p>
            </div>
            <div className="admin-card">
              <Gavel size={24} className="text-[#C5A059] mb-4" />
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-2">
                Total Auctions
              </p>
              <p className="font-serif text-4xl text-[#F5F5F0]">{stats.total_auctions}</p>
            </div>
            <div className="admin-card">
              <Users size={24} className="text-[#C5A059] mb-4" />
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-2">
                Total Bids
              </p>
              <p className="font-serif text-4xl text-[#F5F5F0]">{stats.total_bids}</p>
            </div>
            <div className="admin-card">
              <DollarSign size={24} className="text-[#C5A059] mb-4" />
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-2">
                Revenue (Fees)
              </p>
              <p className="font-serif text-4xl text-[#F5F5F0]">${stats.total_revenue}</p>
            </div>
          </div>
        )}

        {/* Artworks Tab */}
        {activeTab === 'artworks' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-serif text-2xl text-[#F5F5F0]">Manage Artworks</h2>
              <Button
                onClick={() => {
                  setEditingArtwork(null);
                  setArtworkForm({ title: '', description: '', image_url: '', year: '', medium: '', dimensions: '' });
                  setArtworkModal(true);
                }}
                className="bg-[#C5A059] text-[#0A0A0A] hover:bg-[#E0BE77]"
                data-testid="add-artwork-btn"
              >
                <Plus size={16} className="mr-2" />
                Add Artwork
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {artworks.map((artwork) => (
                <div key={artwork.id} className="admin-card group">
                  <img
                    src={artwork.image_url}
                    alt={artwork.title}
                    className="w-full h-48 object-cover mb-4"
                  />
                  <h3 className="font-serif text-lg text-[#F5F5F0] mb-2">{artwork.title}</h3>
                  <p className="text-[#A3A3A3] text-sm mb-4 line-clamp-2">{artwork.description}</p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditArtwork(artwork)}
                      className="border-white/20 text-[#F5F5F0] hover:bg-white/10"
                      data-testid={`edit-artwork-${artwork.id}`}
                    >
                      <Edit size={14} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteArtwork(artwork.id)}
                      className="border-[#D84B79]/30 text-[#D84B79] hover:bg-[#D84B79]/10"
                      data-testid={`delete-artwork-${artwork.id}`}
                    >
                      <Trash2 size={14} className="mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Auctions Tab */}
        {activeTab === 'auctions' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-serif text-2xl text-[#F5F5F0]">Manage Auctions</h2>
              <Button
                onClick={() => setAuctionModal(true)}
                className="bg-[#C5A059] text-[#0A0A0A] hover:bg-[#E0BE77]"
                data-testid="add-auction-btn"
              >
                <Plus size={16} className="mr-2" />
                Create Auction
              </Button>
            </div>

            <div className="space-y-4">
              {auctions.map((auction) => (
                <div key={auction.id} className="admin-card flex items-center gap-6">
                  {auction.artwork?.image_url && (
                    <img
                      src={auction.artwork.image_url}
                      alt={auction.artwork.title}
                      className="w-24 h-24 object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-serif text-lg text-[#F5F5F0] mb-1">{auction.title}</h3>
                    <p className="text-[#A3A3A3] text-sm">
                      Ends: {new Date(auction.end_date).toLocaleDateString()}
                    </p>
                    <p className="text-[#C5A059] font-mono text-sm mt-1">
                      Current Bid: ${auction.highest_bid?.toLocaleString()} ({auction.bid_count} bids)
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteAuction(auction.id)}
                    className="border-[#D84B79]/30 text-[#D84B79] hover:bg-[#D84B79]/10"
                    data-testid={`delete-auction-${auction.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
              {auctions.length === 0 && (
                <p className="text-[#525252] text-center py-12">No auctions yet</p>
              )}
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div>
            <h2 className="font-serif text-2xl text-[#F5F5F0] mb-8">Contact Messages</h2>
            <div className="space-y-4">
              {messages.map((message) => (
                <div key={message.id} className="admin-card">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-[#F5F5F0] font-medium">{message.name}</h3>
                      <p className="text-[#A3A3A3] text-sm">{message.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!message.read && (
                        <span className="px-2 py-1 bg-[#C5A059]/20 text-[#C5A059] text-xs font-mono">
                          NEW
                        </span>
                      )}
                      <span className="text-[#525252] text-xs">
                        {new Date(message.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <p className="text-[#C5A059] text-sm font-medium mb-2">{message.subject}</p>
                  <p className="text-[#A3A3A3] text-sm">{message.message}</p>
                </div>
              ))}
              {messages.length === 0 && (
                <p className="text-[#525252] text-center py-12">No messages yet</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Artwork Modal */}
      <Dialog open={artworkModal} onOpenChange={setArtworkModal}>
        <DialogContent className="bg-[#121212] border border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#F5F5F0]">
              {editingArtwork ? 'Edit Artwork' : 'Add Artwork'}
            </DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">
              Fill in the artwork details below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleArtworkSubmit} className="space-y-4 mt-4">
            <Input
              placeholder="Title"
              value={artworkForm.title}
              onChange={(e) => setArtworkForm({ ...artworkForm, title: e.target.value })}
              required
              className="bg-transparent border-white/20"
              data-testid="artwork-title"
            />
            <Input
              placeholder="Image URL"
              value={artworkForm.image_url}
              onChange={(e) => setArtworkForm({ ...artworkForm, image_url: e.target.value })}
              required
              className="bg-transparent border-white/20"
              data-testid="artwork-image-url"
            />
            <Textarea
              placeholder="Description"
              value={artworkForm.description}
              onChange={(e) => setArtworkForm({ ...artworkForm, description: e.target.value })}
              required
              className="bg-transparent border-white/20"
              data-testid="artwork-description"
            />
            <div className="grid grid-cols-3 gap-4">
              <Input
                placeholder="Year"
                value={artworkForm.year}
                onChange={(e) => setArtworkForm({ ...artworkForm, year: e.target.value })}
                className="bg-transparent border-white/20"
              />
              <Input
                placeholder="Medium"
                value={artworkForm.medium}
                onChange={(e) => setArtworkForm({ ...artworkForm, medium: e.target.value })}
                className="bg-transparent border-white/20"
              />
              <Input
                placeholder="Dimensions"
                value={artworkForm.dimensions}
                onChange={(e) => setArtworkForm({ ...artworkForm, dimensions: e.target.value })}
                className="bg-transparent border-white/20"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#C5A059] text-[#0A0A0A] hover:bg-[#E0BE77]"
              data-testid="artwork-submit"
            >
              {editingArtwork ? 'Update Artwork' : 'Add Artwork'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Auction Modal */}
      <Dialog open={auctionModal} onOpenChange={setAuctionModal}>
        <DialogContent className="bg-[#121212] border border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#F5F5F0]">
              Create Auction
            </DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">
              Set up a new monthly auction.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAuctionSubmit} className="space-y-4 mt-4">
            <div>
              <label className="text-[#A3A3A3] text-sm block mb-2">Select Artwork</label>
              <select
                value={auctionForm.artwork_id}
                onChange={(e) => setAuctionForm({ ...auctionForm, artwork_id: e.target.value })}
                required
                className="w-full bg-transparent border border-white/20 p-2 text-[#F5F5F0]"
                data-testid="auction-artwork-select"
              >
                <option value="">Choose an artwork</option>
                {artworks.map((art) => (
                  <option key={art.id} value={art.id} className="bg-[#121212]">
                    {art.title}
                  </option>
                ))}
              </select>
            </div>
            <Input
              placeholder="Auction Title"
              value={auctionForm.title}
              onChange={(e) => setAuctionForm({ ...auctionForm, title: e.target.value })}
              required
              className="bg-transparent border-white/20"
              data-testid="auction-title"
            />
            <Textarea
              placeholder="Description"
              value={auctionForm.description}
              onChange={(e) => setAuctionForm({ ...auctionForm, description: e.target.value })}
              required
              className="bg-transparent border-white/20"
              data-testid="auction-description"
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[#A3A3A3] text-sm block mb-2">Start Date</label>
                <Input
                  type="datetime-local"
                  value={auctionForm.start_date}
                  onChange={(e) => setAuctionForm({ ...auctionForm, start_date: e.target.value })}
                  required
                  className="bg-transparent border-white/20"
                  data-testid="auction-start-date"
                />
              </div>
              <div>
                <label className="text-[#A3A3A3] text-sm block mb-2">End Date</label>
                <Input
                  type="datetime-local"
                  value={auctionForm.end_date}
                  onChange={(e) => setAuctionForm({ ...auctionForm, end_date: e.target.value })}
                  required
                  className="bg-transparent border-white/20"
                  data-testid="auction-end-date"
                />
              </div>
            </div>
            <Input
              type="number"
              placeholder="Starting Bid ($)"
              value={auctionForm.starting_bid}
              onChange={(e) => setAuctionForm({ ...auctionForm, starting_bid: e.target.value })}
              required
              className="bg-transparent border-white/20"
              data-testid="auction-starting-bid"
            />
            <Button
              type="submit"
              className="w-full bg-[#C5A059] text-[#0A0A0A] hover:bg-[#E0BE77]"
              data-testid="auction-submit"
            >
              Create Auction
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
